/**
 * PresenceBeacon — marks the current user "online in the CRM" (has a tab open) so
 * hot-lead call alerts route to whoever is actually present, independent of
 * whether the Twilio softphone is loaded. Mounted ONCE in CRMApp; renders nothing.
 *
 * Beats POST /api/crm/presence/beat every ~20s (+ immediately on tab focus), well
 * inside the server's 90s presence TTL even when a backgrounded tab throttles the
 * interval toward ~60s. This writes a SEPARATE Redis key from the softphone
 * heartbeat, so the two never clobber each other. Fully defensive: a failed beat
 * is swallowed and never disrupts the CRM.
 */
import { useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const BEAT_MS = 20_000;

export default function PresenceBeacon() {
  const { user } = useAuth();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;
    const beat = () => { api.post('/api/crm/presence/beat', {}).catch(() => {}); };
    beat(); // beat immediately so presence is fresh before the first lead lands
    const id = window.setInterval(beat, BEAT_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') beat(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userId]);

  return null;
}
